/**
 * analytics-shared/hooks/queryShim.js
 *
 * Tiny React-Query-compatible useQuery / useMutation shim.
 * Lets us write hooks today in the exact shape they will have once
 * @tanstack/react-query is installed — then the swap is a single import.
 *
 * Intentionally minimal: cache-by-key, staleTime, refetch, mutate,
 * prefix-based invalidate that actually refires active queries.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// cache: key → { data, ts, subscribers: Set<{ rerender, refetch }> }
// Each subscriber carries its own refetch fn so invalidateQueries can
// actually re-fire the queryFn for every currently-mounted listener
// rather than just marking the entry stale and hoping somebody
// re-renders.
const cache = new Map();
const inflight = new Map(); // key -> Promise

const keyOf = (queryKey) => JSON.stringify(queryKey);

function notify(key) {
  const entry = cache.get(key);
  if (!entry) return;
  entry.subscribers.forEach((sub) => sub.rerender && sub.rerender());
}

/**
 * Prefix-match invalidation. `prefix` is an array; any cache key whose
 * JSON-serialised form starts with the prefix's serialised form (minus
 * the closing bracket) is considered a match.
 *
 * Behaviour:
 *   1. Flip ts=0 so getters report "stale".
 *   2. Call refetch() on every mounted subscriber — that re-fires the
 *      queryFn, which is what the consumer expected `invalidateQueries`
 *      to do in the first place.
 *
 * Without step 2 the queryFn never re-runs unless the component
 * remounts or its key changes, which is why favorites/folders/visibility
 * felt frozen until page reload.
 */
export function invalidateQueries(prefix) {
  const target = JSON.stringify(prefix);
  // Drop the trailing `]` so `["reports"` matches both `["reports"]`
  // and `["reports","list"]`. Same shape React Query uses.
  const matchPrefix = target.slice(0, target.length - 1);
  cache.forEach((entry, key) => {
    if (!key.startsWith(matchPrefix)) return;
    entry.ts = 0;
    // Fire each subscriber's refetch — they each capture their own
    // queryFn closure, so we don't have to track functions globally.
    entry.subscribers.forEach((sub) => {
      if (sub.refetch) sub.refetch();
    });
    // Subscribers without a refetch (legacy) at least get a rerender so
    // they re-read whatever's in cache.
    notify(key);
  });
}

export function setQueryData(queryKey, data) {
  const key = keyOf(queryKey);
  const entry = cache.get(key) || { subscribers: new Set() };
  entry.data = data;
  entry.ts = Date.now();
  cache.set(key, entry);
  notify(key);
}

export function getQueryData(queryKey) {
  return cache.get(keyOf(queryKey))?.data;
}

export function useQuery({
  queryKey, queryFn, enabled = true, staleTime = 30_000,
  // refetchOnMount=true mirrors React Query's default — every fresh
  // page entry kicks off a queryFn call if the cache is stale. Page-
  // level consumers (ReportViewPage, etc.) rely on this to pick up
  // CRM mutations made on other tabs/devices.
  refetchOnMount = true,
}) {
  const key = keyOf(queryKey);
  const [, force] = useState(0);
  const [error, setError] = useState(null);
  const [isFetching, setFetching] = useState(false);
  const mounted = useRef(true);
  // Track the latest queryFn in a ref so invalidate-triggered refetch
  // always calls the most recent closure (useEffect dependencies are
  // [key, enabled, staleTime] — queryFn intentionally not included).
  const queryFnRef = useRef(queryFn);
  queryFnRef.current = queryFn;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Shared executor — used both by the initial-mount fetch and by
  // invalidate-triggered refetch. Sets in-flight/error/state and
  // dedupes via the `inflight` map so two concurrent subscribers
  // (settings panel + canvas, both wanting the same widget) make one
  // network call together.
  const runQuery = useCallback(() => {
    setFetching(true);
    const existing = inflight.get(key)
      || Promise.resolve(queryFnRef.current()).finally(() => inflight.delete(key));
    inflight.set(key, existing);
    return existing
      .then((data) => {
        if (!mounted.current) return data;
        setQueryData(queryKey, data);
        setError(null);
        return data;
      })
      .catch((e) => {
        if (!mounted.current) return undefined;
        setError(e);
        throw e;
      })
      .finally(() => mounted.current && setFetching(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!enabled) return undefined;
    let entry = cache.get(key);
    if (!entry) {
      entry = { subscribers: new Set(), ts: 0, data: undefined };
      cache.set(key, entry);
    }
    const rerender = () => mounted.current && force((n) => n + 1);
    // Register both callbacks so invalidateQueries can refire this
    // subscriber's queryFn.
    const sub = { rerender, refetch: () => { runQuery().catch(() => {}); } };
    entry.subscribers.add(sub);

    const isFresh = entry.data !== undefined && Date.now() - entry.ts < staleTime;
    if (!isFresh && refetchOnMount) {
      runQuery().catch(() => {});
    }
    return () => { entry.subscribers.delete(sub); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, staleTime]);

  const refetch = useCallback(() => {
    const entry = cache.get(key);
    if (entry) entry.ts = 0;
    return runQuery();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, runQuery]);

  const entry = cache.get(key);
  return {
    data: entry?.data,
    error,
    isLoading: entry?.data === undefined && isFetching,
    isFetching,
    isSuccess: !!entry?.data && !error,
    refetch,
  };
}

export function useMutation({ mutationFn, onMutate, onSuccess, onError, onSettled }) {
  const [isPending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const mutate = useCallback((variables, options = {}) => {
    setPending(true);
    setError(null);
    // onMutate runs BEFORE the network call so callers can apply
    // optimistic cache updates. Whatever onMutate returns becomes
    // `context` passed through to onError/onSettled — matches
    // React Query's contract so callers can roll back on failure.
    const context = (onMutate?.(variables) ?? options.onMutate?.(variables)) || null;
    return Promise.resolve(mutationFn(variables))
      .then((data) => {
        if (!mounted.current) return data;
        onSuccess?.(data, variables, context);
        options.onSuccess?.(data, variables, context);
        return data;
      })
      .catch((e) => {
        if (mounted.current) setError(e);
        onError?.(e, variables, context);
        options.onError?.(e, variables, context);
        throw e;
      })
      .finally(() => {
        if (mounted.current) setPending(false);
        onSettled?.(variables, context);
        options.onSettled?.(variables, context);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationFn]);

  return { mutate, mutateAsync: mutate, isPending, error };
}
